import { Capacitor } from '@capacitor/core';
import { SpeechRecognition } from '@capgo/capacitor-speech-recognition';

type PermissionState = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';

interface NativePermissionStatus {
  speechRecognition?: PermissionState;
  microphone?: PermissionState;
}

interface NativeStartResult {
  matches?: string[];
}

interface SpeechResultItem {
  transcript: string;
}

interface SpeechResultEntry {
  isFinal: boolean;
  0: SpeechResultItem;
}

interface SpeechResultEvent {
  resultIndex: number;
  results: SpeechResultEntry[];
}

interface WebSpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  start: () => void;
  stop: () => void;
}

type WebSpeechRecognitionConstructor = new () => WebSpeechRecognition;

interface ExtendedWindow extends Window {
  webkitSpeechRecognition?: WebSpeechRecognitionConstructor;
  SpeechRecognition?: WebSpeechRecognitionConstructor;
}

export interface UnifiedSpeechCallbacks {
  onPartial: (text: string) => void;
  onFinal: (text: string) => void;
  onListeningChange?: (isListening: boolean) => void;
  onPermissionDenied?: () => void;
  onError?: (message: string) => void;
}

const RESTART_DELAY_MS = 250;

const isGrantedOrUnavailable = (value?: PermissionState) => value === undefined || value === 'granted';

const normalizeText = (value: string) => value.trim().replace(/\s+/g, ' ');

const appendUnique = (base: string, next: string) => {
  const normalizedBase = normalizeText(base);
  const normalizedNext = normalizeText(next);
  if (!normalizedNext) return normalizedBase;
  if (!normalizedBase) return normalizedNext;
  if (normalizedBase.endsWith(normalizedNext)) return normalizedBase;
  return `${normalizedBase} ${normalizedNext}`;
};

export class UnifiedSpeechController {
  private readonly isNative: boolean;
  private readonly callbacks: UnifiedSpeechCallbacks;
  private isListening = false;
  private manualStop = false;
  private finalTranscript = '';
  private partialTranscript = '';
  private restartTimer: number | null = null;
  private nativeListeners: Array<{ remove: () => Promise<void> }> = [];
  private webRecognition: WebSpeechRecognition | null = null;

  constructor(callbacks: UnifiedSpeechCallbacks) {
    this.callbacks = callbacks;
    this.isNative = Capacitor.isNativePlatform();
  }

  static isSupported() {
    if (Capacitor.isNativePlatform()) return true;
    const win = window as ExtendedWindow;
    return Boolean(win.webkitSpeechRecognition || win.SpeechRecognition);
  }

  async start() {
    if (this.isListening) return;
    if (!UnifiedSpeechController.isSupported()) {
      throw new Error('Speech recognition is not supported in this environment.');
    }

    this.manualStop = false;
    this.finalTranscript = '';
    this.partialTranscript = '';
    this.isListening = true;
    this.callbacks.onListeningChange?.(true);

    if (this.isNative) {
      await this.startNative();
      return;
    }

    this.startWeb();
  }

  async stop() {
    this.manualStop = true;
    this.clearRestartTimer();

    if (this.isNative) {
      try {
        await SpeechRecognition.stop();
      } catch (error) {
        // Native plugins may throw when already stopped.
      }
      await this.removeNativeListeners();
    } else if (this.webRecognition) {
      try {
        this.webRecognition.stop();
      } catch (error) {
        // Browser recognizer may throw when already stopped.
      }
      this.webRecognition = null;
    }

    this.finishListening();
  }

  async destroy() {
    await this.stop();
  }

  getPendingTranscript() {
    return this.partialTranscript;
  }

  private finishListening() {
    this.isListening = false;
    this.callbacks.onListeningChange?.(false);
  }

  private clearRestartTimer() {
    if (!this.restartTimer) return;
    window.clearTimeout(this.restartTimer);
    this.restartTimer = null;
  }

  private scheduleRestart(fn: () => Promise<void> | void, delayMs = RESTART_DELAY_MS) {
    if (this.restartTimer || !this.isListening || this.manualStop) return;
    this.restartTimer = window.setTimeout(async () => {
      this.restartTimer = null;
      if (!this.isListening || this.manualStop) return;
      try {
        await fn();
      } catch (error) {
        this.callbacks.onError?.('Speech recognition restart failed.');
        this.scheduleRestart(fn, RESTART_DELAY_MS * 2);
      }
    }, delayMs);
  }

  private async startNative() {
    const availability = await SpeechRecognition.available();
    if (!availability.available) {
      this.finishListening();
      throw new Error('Speech recognition is not available on this device.');
    }

    const currentPermissions = (await SpeechRecognition.checkPermissions()) as NativePermissionStatus;
    if (!isGrantedOrUnavailable(currentPermissions.speechRecognition) || !isGrantedOrUnavailable(currentPermissions.microphone)) {
      const requestedPermissions = (await SpeechRecognition.requestPermissions()) as NativePermissionStatus;
      if (!isGrantedOrUnavailable(requestedPermissions.speechRecognition) || !isGrantedOrUnavailable(requestedPermissions.microphone)) {
        this.callbacks.onPermissionDenied?.();
        await this.stop();
        return;
      }
    }

    await this.removeNativeListeners();

    const partialListener = await SpeechRecognition.addListener('partialResults', (data: { matches?: string[] }) => {
      const latest = normalizeText(data.matches?.[0] || '');
      if (!latest || this.manualStop || !this.isListening) return;
      this.partialTranscript = latest;
      this.callbacks.onPartial(latest);
    });

    const stateListener = await SpeechRecognition.addListener('listeningState', (data: { status?: string }) => {
      if (data.status !== 'stopped' || this.manualStop || !this.isListening) return;
      this.scheduleRestart(() => this.startNativeSession());
    });

    this.nativeListeners = [partialListener, stateListener];
    await this.startNativeSession();
  }

  private async startNativeSession() {
    const result = (await SpeechRecognition.start({
      language: 'en-US',
      maxResults: 1,
      partialResults: true,
      popup: false
    })) as NativeStartResult;

    const latest = normalizeText(result.matches?.[0] || '');
    if (!latest || this.manualStop || !this.isListening) return;
    this.finalTranscript = appendUnique(this.finalTranscript, latest);
    this.partialTranscript = '';
    this.callbacks.onFinal(latest);
  }

  private async removeNativeListeners() {
    if (this.nativeListeners.length > 0) {
      await Promise.all(this.nativeListeners.map(listener => listener.remove().catch(() => undefined)));
    }
    this.nativeListeners = [];
    try {
      await SpeechRecognition.removeAllListeners();
    } catch (error) {
      // Ignore cleanup failure.
    }
  }

  private startWeb() {
    const win = window as ExtendedWindow;
    const RecognitionCtor = win.webkitSpeechRecognition || win.SpeechRecognition;
    if (!RecognitionCtor) {
      throw new Error('Speech recognition is not supported in this browser.');
    }

    const recognition = new RecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: SpeechResultEvent) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = normalizeText(event.results[i][0]?.transcript || '');
        if (!transcript) continue;
        if (event.results[i].isFinal) {
          this.finalTranscript = appendUnique(this.finalTranscript, transcript);
          this.partialTranscript = '';
          this.callbacks.onFinal(transcript);
        } else {
          interim = appendUnique(interim, transcript);
        }
      }

      if (!interim || this.manualStop || !this.isListening) return;
      this.partialTranscript = interim;
      this.callbacks.onPartial(interim);
    };

    recognition.onend = () => {
      if (this.manualStop || !this.isListening) {
        this.finishListening();
        return;
      }
      this.scheduleRestart(() => {
        this.webRecognition?.start();
      });
    };

    recognition.onerror = (event: { error: string }) => {
      if (event.error === 'not-allowed') {
        this.callbacks.onPermissionDenied?.();
        void this.stop();
        return;
      }

      if (this.manualStop || !this.isListening) {
        return;
      }

      this.callbacks.onError?.(`Web speech recognition error: ${event.error}`);
      this.scheduleRestart(() => {
        this.webRecognition?.start();
      }, event.error === 'no-speech' ? 200 : RESTART_DELAY_MS);
    };

    this.webRecognition = recognition;
    recognition.start();
  }
}
